
/**
 * Composant de navigation permettant de se déplacer entre les pages
 * avec des boutons première page, page précédente, page suivante et dernière page.
 * 
 * @component
 * @param {Object} props - Les propriétés du composant
 * @param {number} props.currentPage - La page actuelle
 * @param {number} props.totalPages - Le nombre total de pages
 * @param {function} props.onPageChange - Fonction appelée lors du changement de page
 * @param {function} props.onHover - Fonction appelée lors du survol des boutons
 */
import React, { useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PaginationNavigationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onHover: (direction: 'next' | 'previous') => void;
}

export const PaginationNavigation = React.memo(({
  currentPage,
  totalPages,
  onPageChange,
  onHover
}: PaginationNavigationProps) => {
  const handleHoverPrevious = useCallback(() => onHover('previous'), [onHover]);
  const handleHoverNext = useCallback(() => onHover('next'), [onHover]);
  const handleFirstPage = useCallback(() => onPageChange(0), [onPageChange]);
  const handlePreviousPage = useCallback(() => onPageChange(Math.max(0, currentPage - 1)), [onPageChange, currentPage]);
  const handleNextPage = useCallback(() => onPageChange(Math.min(totalPages - 1, currentPage + 1)), [onPageChange, currentPage, totalPages]);
  const handleLastPage = useCallback(() => onPageChange(totalPages - 1), [onPageChange, totalPages]);

  return (
    <div 
      className="flex items-center gap-2"
      role="group"
      aria-label="Navigation des pages"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            onClick={handleFirstPage}
            disabled={currentPage === 0}
            className="w-8 h-8 sm:w-10 sm:h-10 p-0 transition-transform hover:scale-105"
            aria-label="Aller à la première page"
            aria-disabled={currentPage === 0}
          >
            <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Première page</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Aller à la première page</p>
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            onClick={handlePreviousPage}
            onMouseEnter={handleHoverPrevious}
            disabled={currentPage === 0}
            className="w-8 h-8 sm:w-10 sm:h-10 p-0 transition-transform hover:scale-105"
            aria-label="Page précédente"
            aria-disabled={currentPage === 0}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Page précédente</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Page précédente</p>
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            onClick={handleNextPage}
            onMouseEnter={handleHoverNext}
            disabled={currentPage === totalPages - 1}
            className="w-8 h-8 sm:w-10 sm:h-10 p-0 transition-transform hover:scale-105"
            aria-label="Page suivante"
            aria-disabled={currentPage === totalPages - 1}
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Page suivante</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Page suivante</p>
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            onClick={handleLastPage}
            disabled={currentPage === totalPages - 1}
            className="w-8 h-8 sm:w-10 sm:h-10 p-0 transition-transform hover:scale-105"
            aria-label="Aller à la dernière page"
            aria-disabled={currentPage === totalPages - 1}
          >
            <ChevronsRight className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Dernière page</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Aller à la dernière page</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
});

PaginationNavigation.displayName = 'PaginationNavigation';
